import React, { useState } from "react";
import { Flame, MapPin, ImageIcon, Copy, Check, ExternalLink } from "lucide-react";

// Full updated menu — grouped by price tier
const TIERS = [
  {
    price: "$14",
    label: "Mains & Wood-Fired",
    items: [
      "Smokey AF BBQ Pizza",
      "Nacho Jalapeño Pork Pizza",
      "Pepperoni Pizza",
      "Chicken Alfredo Pizza",
      "Chicken Bacon Ranch Pizza",
      "BBQ Boat",
      "Pulled Pork Sandwich",
      "Japanese BBQ Burger",
      "Loaded Pork Fries",
    ],
  },
  {
    price: "$12",
    label: "Wings & Pretzel",
    items: [
      "Bavarian Behemoth Pretzel",
      "Garlic Parmesan Wings",
      "BBQ Wings",
      "Buffalo Wings",
      "Chipotle Firecracker Wings",
    ],
  },
  {
    price: "$10",
    label: "Bowls & Sandwiches",
    items: [
      "BBQ Chicken Sandwich",
      "Pork Crack Mash Bowl",
      "Nacho Jalapeño Pork Sandwich",
      "Japanese Pork Sandwich",
    ],
  },
  {
    price: "$8 / $7 / $5",
    label: "Fusion · Minis · Sides",
    items: ["BBQ Sushi · $8", "Mini Pulled Pork · $7", "Mini BBQ Boat · $7", "Order of Fries · $5"],
  },
];

// Food photo grid (top of the ad)
const PHOTOS = [
  "/menu/bbq_boat_hero.png",
  "/menu/bbq_pizza.png",
  "/menu/loaded_pork_fries.png",
  "/menu/bbq_wings.png",
  "/menu/bbq_sushi.png",
  "/menu/pork_crack_mash_bowl.png",
];

// Facebook caption copy
const CAPTION = `🔥 SMOKEY AF BBQ — NOW OPEN IN FREMONT 🔥

22 reasons to skip the diet this week. Hand-pulled. Wood-fired. Honest as hell.

🍕 PIZZAS & MAINS — $14
Smokey AF BBQ Pizza · Nacho Jalapeño Pork Pizza · Pepperoni · Chicken Alfredo · Chicken Bacon Ranch · BBQ Boat · Pulled Pork Sandwich · Japanese BBQ Burger · Loaded Pork Fries

🥨 THE BEHEMOTH — $12
Bavarian Behemoth Pretzel (shareable · BBQ butter · beer-cheese dip)

🍗 WINGS — $12
Garlic Parmesan · BBQ · Buffalo · Chipotle Firecracker

🥣 SANDWICHES & BOWLS — $10
BBQ Chicken Sandwich · Pork Crack Mash Bowl · Nacho Jalapeño Pork Sandwich · Japanese Pork Sandwich

🍣 FUSION · MINIS · SIDES
BBQ Sushi $8 · Mini Pulled Pork $7 · Mini BBQ Boat $7 · Order of Fries $5

📍 Inside Southern Style Bar — 648 N Broad St, Fremont, NE
👉 Order online: smokey-af-bbq.emergent.host

#SmokeyAFBBQ #FremontEats #BBQ #PulledPork #WoodFired #FremontNE`;

// Reusable ad card component. `scale` controls render size:
//   scale=1   → 540×675 (preview)
//   scale=2   → 1080×1350 (full Facebook resolution for export)
export function MenuAdV2Card({ scale = 1 }) {
  const W = 540 * scale;
  const H = 675 * scale;
  const s = (n) => n * scale; // scale helper for sizes

  return (
    <div
      data-testid="menu-ad-v2-card"
      className="relative overflow-hidden shadow-[0_30px_80px_rgba(0,0,0,0.6)]"
      style={{
        width: W,
        height: H,
        background:
          "radial-gradient(ellipse at 50% -5%, #2a0a08 0%, #0a0606 50%, #000 100%)",
      }}
    >
      {/* Grain */}
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-[0.22] mix-blend-screen pointer-events-none z-[5]"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='220' height='220'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/><feColorMatrix values='0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.08 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
        }}
      />

      {/* Ember glow */}
      <div
        aria-hidden="true"
        className="absolute rounded-full pointer-events-none z-[2]"
        style={{
          top: s(-80),
          left: s(-80),
          width: s(260),
          height: s(260),
          background:
            "radial-gradient(circle, rgba(255,59,48,0.35) 0%, transparent 70%)",
          filter: `blur(${s(20)}px)`,
        }}
      />
      <div
        aria-hidden="true"
        className="absolute rounded-full pointer-events-none z-[2]"
        style={{
          bottom: s(-96),
          right: s(-96),
          width: s(300),
          height: s(300),
          background:
            "radial-gradient(circle, rgba(255,140,0,0.25) 0%, transparent 70%)",
          filter: `blur(${s(20)}px)`,
        }}
      />

      {/* Top brand bar */}
      <div
        className="absolute flex items-center justify-between z-30"
        style={{ top: s(16), left: s(20), right: s(20) }}
      >
        <div className="flex items-center" style={{ gap: s(8) }}>
          <Flame
            className="text-[#FF3B30] drop-shadow-[0_0_10px_rgba(255,59,48,0.7)]"
            style={{ width: s(18), height: s(18) }}
          />
          <span
            className="font-['Anton'] uppercase text-white tracking-[0.3em]"
            style={{ fontSize: s(13) }}
          >
            Smokey <span className="text-[#FF3B30]">AF</span> BBQ
          </span>
        </div>
        <div
          className="bg-[#FF3B30] text-white font-['Anton'] uppercase tracking-wider rotate-2 shadow-[0_8px_24px_rgba(255,59,48,0.5)]"
          style={{
            fontSize: s(11),
            paddingLeft: s(10),
            paddingRight: s(10),
            paddingTop: s(4),
            paddingBottom: s(4),
          }}
        >
          Now Open · Fremont
        </div>
      </div>

      {/* Photo strip */}
      <div
        className="absolute left-0 right-0 z-20"
        style={{ top: s(48) }}
      >
        <div className="flex" style={{ gap: s(3), paddingLeft: s(20), paddingRight: s(20) }}>
          {PHOTOS.slice(0, 3).map((src, i) => (
            <div
              key={src}
              className="relative overflow-hidden"
              style={{
                width: s(164),
                height: s(110),
                transform: i === 1 ? `translateY(${s(6)}px)` : "translateY(0px)",
              }}
            >
              <img
                src={src}
                alt=""
                className="absolute inset-0 w-full h-full object-cover"
                style={{ filter: "saturate(1.12) contrast(1.05)" }}
              />
              <div
                className="absolute inset-0 pointer-events-none"
                style={{ boxShadow: `inset 0 0 ${s(30)}px rgba(0,0,0,0.5)` }}
              />
            </div>
          ))}
        </div>
        <div
          className="flex"
          style={{ gap: s(3), paddingLeft: s(20), paddingRight: s(20), marginTop: s(3) }}
        >
          {PHOTOS.slice(3, 6).map((src, i) => (
            <div
              key={src}
              className="relative overflow-hidden"
              style={{
                width: s(164),
                height: s(82),
                transform: i === 1 ? `translateY(${s(-6)}px)` : "translateY(0px)",
              }}
            >
              <img
                src={src}
                alt=""
                className="absolute inset-0 w-full h-full object-cover"
                style={{ filter: "saturate(1.12) contrast(1.05)" }}
              />
              <div
                className="absolute inset-0 pointer-events-none"
                style={{ boxShadow: `inset 0 0 ${s(30)}px rgba(0,0,0,0.5)` }}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Fade into menu */}
      <div
        aria-hidden="true"
        className="absolute left-0 right-0 z-[21] pointer-events-none"
        style={{
          top: s(195),
          height: s(40),
          background:
            "linear-gradient(to bottom, transparent 0%, #0a0606 100%)",
        }}
      />

      {/* Headline */}
      <div
        className="absolute z-30"
        style={{ top: s(240), left: s(20), right: s(20) }}
      >
        <div className="flex items-center" style={{ gap: s(8), marginBottom: s(8) }}>
          <span className="bg-[#FF8C00]" style={{ height: 1, width: s(24) }} />
          <span
            className="font-['Outfit'] uppercase text-[#FF8C00] tracking-[0.4em]"
            style={{ fontSize: s(9) }}
          >
            The Full Menu
          </span>
        </div>
        <h1
          className="font-['Anton'] uppercase text-white leading-[0.85]"
          style={{ fontSize: s(38), letterSpacing: "-0.02em" }}
        >
          22 Reasons to{" "}
          <span style={{ color: "#FF3B30" }}>Skip the Diet.</span>
        </h1>
        <p
          className="font-['Outfit'] italic text-zinc-300"
          style={{ fontSize: s(11), marginTop: s(6) }}
        >
          Hand-pulled. Wood-fired. Honest as hell.
        </p>
      </div>

      {/* Menu grid */}
      <div
        className="absolute z-30"
        style={{ top: s(345), left: s(20), right: s(20) }}
      >
        <div className="grid grid-cols-2" style={{ columnGap: s(20) }}>
          {/* $14 tier */}
          <div className="flex flex-col">
            <div
              className="flex items-baseline justify-between border-b border-[#FF3B30]/60"
              style={{ paddingBottom: s(2), marginBottom: s(6) }}
            >
              <span
                className="font-['Outfit'] uppercase text-zinc-400 tracking-[0.3em]"
                style={{ fontSize: s(8) }}
              >
                {TIERS[0].label}
              </span>
              <span
                className="font-['Anton'] text-[#FF8C00]"
                style={{ fontSize: s(16) }}
              >
                {TIERS[0].price}
              </span>
            </div>
            <ul style={{ display: "flex", flexDirection: "column", gap: s(2) }}>
              {TIERS[0].items.map((it) => (
                <li
                  key={it}
                  className="font-['Outfit'] text-white leading-tight"
                  style={{ fontSize: s(10) }}
                >
                  <span className="text-[#FF3B30]" style={{ marginRight: s(6) }}>›</span>
                  {it}
                </li>
              ))}
            </ul>
          </div>

          {/* Other tiers stacked */}
          <div className="flex flex-col" style={{ gap: s(12) }}>
            {TIERS.slice(1).map((tier) => (
              <div key={tier.price} className="flex flex-col">
                <div
                  className="flex items-baseline justify-between border-b border-[#FF3B30]/60"
                  style={{ paddingBottom: s(2), marginBottom: s(4) }}
                >
                  <span
                    className="font-['Outfit'] uppercase text-zinc-400 tracking-[0.3em]"
                    style={{ fontSize: s(8) }}
                  >
                    {tier.label}
                  </span>
                  <span
                    className="font-['Anton'] text-[#FF8C00]"
                    style={{ fontSize: s(14) }}
                  >
                    {tier.price}
                  </span>
                </div>
                <ul style={{ display: "flex", flexDirection: "column", gap: s(2) }}>
                  {tier.items.map((it) => (
                    <li
                      key={it}
                      className="font-['Outfit'] text-white leading-tight"
                      style={{ fontSize: s(10) }}
                    >
                      <span className="text-[#FF3B30]" style={{ marginRight: s(6) }}>›</span>
                      {it}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom CTA */}
      <div className="absolute bottom-0 left-0 right-0 z-30">
        <div
          className="absolute inset-x-0 bottom-0"
          style={{
            height: s(92),
            background:
              "linear-gradient(to top, #000 0%, rgba(0,0,0,0.95) 60%, transparent 100%)",
          }}
        />
        <div
          className="relative"
          style={{ paddingLeft: s(20), paddingRight: s(20), paddingBottom: s(16), paddingTop: s(8) }}
        >
          <div className="flex items-center justify-between" style={{ gap: s(12) }}>
            <div>
              <div className="flex items-center" style={{ gap: s(6), marginBottom: s(4) }}>
                <MapPin
                  className="text-[#FF8C00]"
                  style={{ width: s(11), height: s(11) }}
                />
                <span
                  className="font-['Outfit'] uppercase text-zinc-400 tracking-[0.3em]"
                  style={{ fontSize: s(8) }}
                >
                  Inside Southern Style Bar
                </span>
              </div>
              <div
                className="font-['Anton'] uppercase text-white tracking-wider"
                style={{ fontSize: s(14) }}
              >
                648 N Broad St · Fremont, NE
              </div>
            </div>
            <div
              className="font-['Anton'] tracking-widest uppercase bg-[#FF3B30] text-white shadow-[0_8px_24px_rgba(255,59,48,0.5)]"
              style={{
                fontSize: s(12),
                paddingLeft: s(12),
                paddingRight: s(12),
                paddingTop: s(8),
                paddingBottom: s(8),
              }}
            >
              Order Online
            </div>
          </div>
          <div
            className="text-center font-['Outfit'] uppercase text-zinc-400 tracking-[0.4em]"
            style={{ fontSize: s(9), marginTop: s(8) }}
          >
            smokey-af-bbq.emergent.host
          </div>
        </div>
      </div>
    </div>
  );
}

// Main page — preview + caption + link to standalone photo
export default function MenuAdV2() {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(CAPTION);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch (e) {
      // Fallback: select the textarea
      const el = document.getElementById("fb-caption-text");
      if (el) {
        el.focus();
        el.select();
      }
    }
  };

  return (
    <div className="min-h-screen bg-zinc-800 flex flex-col items-center p-6 gap-8">
      <p
        data-testid="menu-ad-v2-label"
        className="font-['Outfit'] text-zinc-300 text-sm uppercase tracking-[0.3em] mt-2"
      >
        Facebook Ad · Full Menu · 1080×1350
      </p>

      <MenuAdV2Card scale={1} />

      {/* Open standalone photo link */}
      <a
        href="/brand/menu-ad-v2-photo"
        target="_blank"
        rel="noopener noreferrer"
        data-testid="open-standalone-photo-link"
        className="group inline-flex items-center gap-2 font-['Anton'] text-sm tracking-widest uppercase bg-[#FF3B30] hover:bg-white hover:text-black text-white px-6 py-3 transition-colors shadow-[0_8px_24px_rgba(255,59,48,0.5)]"
      >
        <ImageIcon className="w-4 h-4" />
        Open Photo (1080×1350)
        <ExternalLink className="w-3.5 h-3.5 opacity-70" />
      </a>
      <p className="font-['Outfit'] text-zinc-400 text-xs max-w-md text-center -mt-4">
        Opens the ad alone on a new tab — right-click and &quot;Save image as…&quot; or
        screenshot it for Facebook.
      </p>

      {/* Caption block */}
      <div
        data-testid="fb-caption-block"
        className="w-full max-w-2xl bg-zinc-900 border border-zinc-700 rounded-lg overflow-hidden"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-700 bg-zinc-950">
          <div className="flex items-center gap-2">
            <span className="font-['Anton'] uppercase tracking-[0.25em] text-white text-sm">
              Facebook Caption
            </span>
            <span className="font-['Outfit'] text-zinc-500 text-xs">
              copy & paste into your post
            </span>
          </div>
          <button
            onClick={handleCopy}
            data-testid="copy-caption-btn"
            className="inline-flex items-center gap-1.5 font-['Anton'] uppercase tracking-wider text-xs bg-[#FF3B30] hover:bg-[#ff5448] text-white px-3 py-1.5 transition-colors"
          >
            {copied ? (
              <>
                <Check className="w-3.5 h-3.5" /> Copied
              </>
            ) : (
              <>
                <Copy className="w-3.5 h-3.5" /> Copy
              </>
            )}
          </button>
        </div>
        <textarea
          id="fb-caption-text"
          data-testid="fb-caption-text"
          readOnly
          value={CAPTION}
          className="w-full bg-zinc-900 text-zinc-100 font-['Outfit'] text-sm leading-relaxed p-4 h-72 resize-none focus:outline-none focus:ring-1 focus:ring-[#FF3B30]"
        />
      </div>

      <div className="h-6" />
    </div>
  );
}
